import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from 'react-oidc-context'
import { FluentProvider } from '@fluentui/react-components'
import { loadStripe } from '@stripe/stripe-js'
import { CartProvider } from './contexts/ShopCartContext'
import { cognitoAuthConfig } from './auth/auth-config'
import theme from './theme/theme'
import App from './App.tsx'
import './index.css'

// Kick off the Stripe.js download as early as possible so it can warm up in
// parallel with the rest of the app boot, instead of blocking checkout render.
// The CheckoutPage will await this same promise (re-used via module cache).
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (stripeKey) {
    loadStripe(stripeKey);
}

// Invisible SSO handoff: when the Alyze App redirects a user here for the
// personalized-supplements checkout, it appends the Cognito session as a URL
// fragment (#alyze_session=<base64-json>). Hydrate it into the OIDC storage
// key that this app's UserManager reads from BEFORE AuthProvider mounts, so
// the user appears logged-in with zero flicker and no Cognito round-trip.
// The fragment is never sent to the server.
(function hydrateAlyzeSessionFromFragment() {
    try {
        const hash = window.location.hash;
        console.log('[AlyzeHandoff] checking URL fragment for alyze_session', { hashPresent: !!hash, hashLength: hash?.length ?? 0 });
        if (!hash || !hash.includes('alyze_session=')) {
            console.log('[AlyzeHandoff] no alyze_session fragment present, skipping hydration');
            return;
        }

        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const encoded = params.get('alyze_session');
        if (!encoded) {
            console.warn('[AlyzeHandoff] alyze_session key found but value is empty');
            return;
        }

        const json = decodeURIComponent(escape(atob(encoded)));
        // Validate it parses as JSON before writing.
        const parsed = JSON.parse(json);
        console.log('[AlyzeHandoff] decoded session payload', {
            hasAccessToken: !!parsed.access_token,
            hasIdToken: !!parsed.id_token,
            hasRefreshToken: !!parsed.refresh_token,
            expiresAt: parsed.expires_at ? new Date(parsed.expires_at * 1000).toISOString() : null,
            profileSub: parsed.profile?.sub,
            profileEmail: parsed.profile?.email,
            profileGroups: parsed.profile?.['cognito:groups'],
        });

        const authority = cognitoAuthConfig.authority;
        const clientId = cognitoAuthConfig.client_id;
        if (authority && clientId) {
            const storageKey = `oidc.user:${authority}:${clientId}`;
            // react-oidc-context defaults to sessionStorage. Write to BOTH so
            // we work regardless of where AuthProvider is configured to read.
            sessionStorage.setItem(storageKey, json);
            localStorage.setItem(storageKey, json);
            console.log('[AlyzeHandoff] wrote session to sessionStorage + localStorage under key:', storageKey);
        } else {
            console.warn('[AlyzeHandoff] missing authority or clientId in cognitoAuthConfig, cannot write storage', { authority, clientId });
        }

        // Strip the fragment so it doesn't leak into bookmarks/refresh/logs.
        params.delete('alyze_session');
        const remainingHash = params.toString();
        const cleanUrl = window.location.pathname + window.location.search + (remainingHash ? `#${remainingHash}` : '');
        window.history.replaceState(null, '', cleanUrl);
    } catch (err) {
        // Non-fatal: if hydration fails the user will just be prompted to sign in normally.
        console.warn('[AlyzeHandoff] Failed to hydrate Alyze session from URL fragment:', err);
    }
})();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter future={{ 
      v7_startTransition: true,
      v7_relativeSplatPath: true 
    }}>
      <AuthProvider {...cognitoAuthConfig}>
        <FluentProvider theme={theme}>
          <CartProvider>
            <App />
          </CartProvider>
        </FluentProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
